import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";

import { Icon, Button } from "@/design-system";

/**
 * The landing hero — the reference HOME's banner carousel (grep
 * `<!-- ===== HOME ===== -->` in
 * `resources/Dental supply storefront design system/Dens N Dente Tailwind.dc.html`):
 * a full-bleed scroll-snap strip whose **first slide is the brand hero** — blue-900
 * radial gradient, the "Everything your clinic needs, supplied with care." headline,
 * signed-in greeting / signed-out trust eyebrow, "Shop the catalogue" + a conditional
 * secondary CTA, three trust chips, the white logo mark — with promo slides after it,
 * then the reference's own arrows and dots.
 *
 * The promo slides are the reference's own `promoBanners`, **copy verbatim per the
 * fidelity bar and explicit request** — gradient background, eyebrow + icon, oversized
 * title, white pill CTA, 210px icon circle, optional logo-mark watermark. Only the
 * targets are mapped onto real routes (`deals` → `/clearance`, `shop` → `/categories`,
 * `reorder` → `/re-order`, `about` → `/about`).
 *
 * ⚠️ The slide copy is the design fixture's marketing — "Free delivery on every order,
 * no minimum through June", "PDC 2026 booth 412". **Have marketing/legal vet these
 * claims before a release**; the copy lives only in `PROMO_SLIDES` below.
 *
 * (An earlier iteration filled these slots with the retired `HeroCarousel`'s real
 * image banners; replaced with the reference slides on request.)
 *
 * Other divergences, all deliberate:
 *
 * - The headline's `.dnd-line` reveal animation isn't vendored (it lives in the
 *   prototype's `global.css`); the type renders statically.
 * - Signed-out's "Open an account" goes to Auth0 signup
 *   (`loginWithRedirect({ screen_hint: "signup" })`, `AuthButton`'s own pattern) —
 *   there is no application page (`goApply` in the reference).
 * - "Shop the catalogue" → `/categories`, the reference's `goShop`.
 * - Auto-advance every 7s is kept from the old carousel (the reference prototype has
 *   no timer); any manual interaction stops it, so it can't fight the user's scroll.
 *
 * The greeting uses `state.user.info.firstname` when present — NetSuite's lowercase
 * field — and falls back to the plain "Welcome back".
 */
const PROMO_SLIDES = [
  // {
  //   eyebrow: "Clearance event",
  //   title: "Up to 40% off overstocked essentials",
  //   sub: "Gloves, bibs, and barriers — while they last.",
  //   cta: "Shop clearance",
  //   icon: "sell",
  //   mark: true,
  //   to: "/clearance",
  //   bg: "linear-gradient(120deg, var(--orange-500), var(--orange-600))",
  // },
  // All four promo slides are switched off (Oct 20 2026) — the hero is the
  // brand slide alone. Uncomment one to bring it back; SLIDE_COUNT, the
  // arrows, the dots and the auto-advance all follow the array length, and
  // with a single slide none of that chrome renders.
  // {
  //   eyebrow: "This month only",
  //   title: "Free delivery on every order",
  //   sub: "No minimum through June — we've got the shipping.",
  //   cta: "Start an order",
  //   icon: "local_shipping",
  //   mark: false,
  //   to: "/categories",
  //   bg: "radial-gradient(680px 320px at 82% -50%, var(--blue-700), transparent 60%), var(--blue-900)",
  // },
  // {
  //   eyebrow: "New",
  //   title: "Put your staples on auto-delivery",
  //   sub: "Set it once — we'll keep your operatory stocked.",
  //   cta: "Set up auto-delivery",
  //   icon: "autorenew",
  //   mark: true,
  //   to: "/re-order",
  //   bg: "linear-gradient(120deg, var(--orange-500), var(--orange-600))",
  // },
  // {
  //   eyebrow: "Come say hi",
  //   title: "Visit us at PDC 2026 — booth 412",
  //   sub: "Live demos, show-floor pricing, and a coffee on us.",
  //   cta: "See what's on",
  //   icon: "storefront",
  //   mark: true,
  //   to: "/about",
  //   bg: "linear-gradient(120deg, var(--blue-800), var(--blue-900))",
  // },
];

const SLIDE_COUNT = 1 + PROMO_SLIDES.length;
// One slide is not a carousel: no arrows, no dots, no timer.
const HAS_MULTIPLE_SLIDES = SLIDE_COUNT > 1;
const AUTO_ADVANCE_MS = 7000;

// Two chips (Oct 20 2026): the "30,000+ products in stock" chip was removed
// on request, and the delivery chip no longer promises 1–2 days — "Free
// delivery $300+", matching FREE_SHIPPING_THRESHOLD and NetSuite's ship-item
// rule.
const trustChips = [
  { icon: "local_shipping", label: "Free delivery $300+" },
  { icon: "autorenew", label: "Smart auto-delivery" },
];

const arrowClass =
  "absolute top-1/2 z-[3] inline-flex h-[44px] w-[44px] -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border-0 bg-[rgba(255,255,255,.18)] text-white [transition:background_var(--duration-fast)] hover:bg-[rgba(255,255,255,.3)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]";

export default function HomeHero() {
  const navigate = useNavigate();
  const { isAuthenticated, loginWithRedirect } = useAuth0();
  const firstname = useSelector((state) => state.user?.info?.firstname);
  const trackRef = useRef(null);
  const [page, setPage] = useState(0);
  // Any manual interaction parks the timer for good — an auto-advance that resumes
  // mid-read yanks the slide out from under the user.
  const [autoPlay, setAutoPlay] = useState(true);

  const goTo = (index) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + SLIDE_COUNT) % SLIDE_COUNT;
    track.scrollTo({ left: next * track.clientWidth, behavior: "smooth" });
  };

  useEffect(() => {
    if (!autoPlay || !HAS_MULTIPLE_SLIDES) return undefined;
    const timer = setInterval(() => {
      const track = trackRef.current;
      if (!track) return;
      const current = Math.round(track.scrollLeft / track.clientWidth);
      track.scrollTo({
        left: ((current + 1) % SLIDE_COUNT) * track.clientWidth,
        behavior: "smooth",
      });
    }, AUTO_ADVANCE_MS);
    return () => clearInterval(timer);
  }, [autoPlay]);

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    setPage(
      Math.max(
        0,
        Math.min(
          SLIDE_COUNT - 1,
          Math.round(track.scrollLeft / track.clientWidth)
        )
      )
    );
  };

  const manual = (fn) => () => {
    setAutoPlay(false);
    fn();
  };

  return (
    <section className="relative overflow-hidden">
      <div
        ref={trackRef}
        onScroll={onScroll}
        onPointerDown={() => setAutoPlay(false)}
        className="flex overflow-x-auto [scroll-behavior:smooth] [scroll-snap-type:x_mandatory] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {/* Slide 1 — the brand hero, reference markup */}
        <div className="relative min-w-full flex-[0_0_100%] snap-start overflow-hidden bg-blue-900 text-white [background:radial-gradient(1100px_520px_at_82%_-12%,var(--blue-700),transparent_60%),var(--blue-900)]">
          <div className="relative mx-auto grid max-w-container grid-cols-1 items-center gap-[var(--space-8)] px-[var(--gutter)] pb-[88px] pt-[var(--space-20)] md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] md:gap-[var(--space-14)]">
            <div>
              {/* Eyebrow only for a signed-in customer. The logged-out line
                  ("Trusted by 2,400+ clinics across Canada") was removed on
                  request (Oct 20 2026) — an unverifiable claim. */}
              {isAuthenticated && (
                <span className="inline-flex items-center gap-[var(--space-2)] font-sans text-sm font-semibold uppercase tracking-[var(--tracking-wide)] text-blue-200">
                  <Icon name="waving_hand" size={18} fill weight={400} />
                  {`Welcome back${firstname ? `, ${firstname}` : ""}`}
                </span>
              )}
              <h1 className="mb-[18px] mt-[var(--space-4)] max-w-[20ch] font-sans text-[clamp(34px,3.7vw,52px)] font-semibold leading-[1.12] tracking-[-.01em] text-white">
                <span className="block">Everything your clinic needs,</span>
                <span className="block">
                  supplied with{" "}
                  <em className="font-medium italic text-blue-200">care</em>.
                </span>
              </h1>
              <p className="mb-[var(--space-8)] mt-0 max-w-[52ch] font-sans text-lg leading-[1.55] text-blue-100">
                Order what you need, get it fast, and reach a real person when
                something goes wrong.
              </p>
              <div className="flex flex-wrap gap-[var(--space-3)]">
                <Button
                  size="lg"
                  startIcon="storefront"
                  onClick={() => navigate("/categories")}
                >
                  Shop the catalogue
                </Button>
                {isAuthenticated ? (
                  <Link
                    to="/re-order"
                    className="inline-flex h-[44px] cursor-pointer items-center gap-[var(--space-2)] rounded-pill border-[1.5px] border-[rgba(255,255,255,.4)] bg-[rgba(255,255,255,.04)] px-[var(--space-6)] py-0 font-sans text-base font-semibold text-white no-underline [transition:background_var(--duration-fast),border-color_var(--duration-fast)] hover:border-white hover:bg-[rgba(255,255,255,.12)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                  >
                    <Icon name="history" size={20} weight={400} />
                    Order again
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => loginWithRedirect({ screen_hint: "signup" })}
                    className="inline-flex h-[44px] cursor-pointer items-center gap-[var(--space-2)] rounded-pill border-[1.5px] border-[rgba(255,255,255,.4)] bg-[rgba(255,255,255,.04)] px-[var(--space-6)] py-0 font-sans text-base font-semibold text-white [transition:background_var(--duration-fast),border-color_var(--duration-fast)] hover:border-white hover:bg-[rgba(255,255,255,.12)] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                  >
                    <Icon name="add_business" size={20} weight={400} />
                    Open an account
                  </button>
                )}
              </div>
              <div className="mt-[var(--space-10)] flex flex-wrap gap-[var(--space-7)]">
                {trustChips.map(({ icon, label }) => (
                  <span
                    key={icon}
                    className="inline-flex items-center gap-[var(--space-2)] font-sans text-sm text-blue-100"
                  >
                    <Icon
                      name={icon}
                      size={18}
                      fill
                      weight={400}
                      className="text-blue-200"
                    />
                    {label}
                  </span>
                ))}
              </div>
            </div>
            <div className="hidden justify-center md:flex">
              <img
                src="/logo-mark-white.png"
                alt=""
                aria-hidden="true"
                className="w-[168px] opacity-[.9]"
              />
            </div>
          </div>
        </div>

        {/* The reference's promo slides, markup 1:1 */}
        {PROMO_SLIDES.map((slide) => (
          <div
            key={slide.title}
            className="relative min-w-full flex-[0_0_100%] snap-start overflow-hidden text-white"
            style={{ background: slide.bg }}
          >
            {slide.mark && (
              <img
                src="/logo-mark-white.png"
                alt=""
                aria-hidden="true"
                className="pointer-events-none absolute bottom-[-70px] right-[-30px] w-[430px] opacity-[.08]"
              />
            )}
            <div className="relative mx-auto grid max-w-container grid-cols-1 items-center gap-[var(--space-8)] px-[var(--gutter)] pb-[104px] pt-[88px] md:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)] md:gap-[var(--space-14)]">
              <div>
                <span className="inline-flex items-center gap-[var(--space-2)] font-sans text-sm font-semibold uppercase tracking-[var(--tracking-wide)] text-[rgba(255,255,255,.88)]">
                  <Icon name={slide.icon} size={18} fill weight={400} />
                  {slide.eyebrow}
                </span>
                <h2 className="mx-0 my-[18px] max-w-[18ch] font-sans text-[clamp(38px,4.8vw,62px)] font-semibold leading-[1.05] tracking-[-.01em] text-white">
                  {slide.title}
                </h2>
                <p className="mb-[30px] mt-0 max-w-[48ch] font-sans text-lg leading-[1.55] text-[rgba(255,255,255,.92)]">
                  {slide.sub}
                </p>
                <Link
                  to={slide.to}
                  className="inline-flex h-[44px] items-center gap-[var(--space-2)] rounded-pill border-0 bg-white px-[26px] font-sans text-base font-bold text-blue-900 no-underline [transition:background_var(--duration-fast),transform_var(--duration-fast)] hover:bg-neutral-50 active:scale-[.96] focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
                >
                  {slide.cta}
                  <Icon name="arrow_forward" size={20} weight={400} />
                </Link>
              </div>
              <div className="hidden justify-center md:flex">
                <span className="inline-flex h-[210px] w-[210px] items-center justify-center rounded-full border border-[rgba(255,255,255,.2)] bg-[rgba(255,255,255,.12)]">
                  <Icon
                    name={slide.icon}
                    size={108}
                    weight={400}
                    className="text-white"
                  />
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Reference chrome: circle arrows + dots — only when there is
          somewhere to go. */}
      {HAS_MULTIPLE_SLIDES && (
        <>
          <button
            type="button"
            aria-label="Previous banner"
            onClick={manual(() => goTo(page - 1))}
            className={`${arrowClass} left-[20px]`}
          >
            <Icon name="arrow_back" size={24} weight={400} />
          </button>
          <button
            type="button"
            aria-label="Next banner"
            onClick={manual(() => goTo(page + 1))}
            className={`${arrowClass} right-[20px]`}
          >
            <Icon name="arrow_forward" size={24} weight={400} />
          </button>
          <div className="absolute bottom-[30px] left-0 right-0 z-[3] flex items-center justify-center gap-[11px]">
            {Array.from({ length: SLIDE_COUNT }, (_, index) => (
              <button
                key={`hero-dot-${index}`}
                type="button"
                aria-label={`Go to banner ${index + 1}`}
                onClick={manual(() => goTo(index))}
                className={`h-[11px] cursor-pointer rounded-pill border-0 p-0 [transition:width_var(--duration-base)_var(--ease-out),background_var(--duration-fast)] ${
                  index === page
                    ? "w-[30px] bg-white"
                    : "w-[11px] bg-[rgba(255,255,255,.45)] hover:bg-[rgba(255,255,255,.7)]"
                }`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}
